import clsx from "clsx";
import type { Icons } from "./icons";
import styles from "./Icon.module.css";

declare module "csstype" {
  interface Properties {
    "--icon-animation-duration"?: string;
  }
}

export interface IconBaseProps {
  size?: number;
  width?: number;
  height?: number;
  className?: string;
  animation?: IconAnimation;
  duration?: number; // in ms
  tabIndex?: number;
  style?: React.CSSProperties;
  ref?: React.Ref<SVGSVGElement>;
  onClick?: (event: React.MouseEvent) => void;
}

export interface IconProps extends IconBaseProps {
  name: keyof Icons;
  icon?: never;
}

interface IconFabricProps {
  name?: never;
  icon: keyof Icons | IconProps;
  className?: string;
}

type IconAnimation = "spin" | "swipe";

interface Icon {
  (props: IconProps): React.ReactNode;
  (props: IconFabricProps): React.ReactNode;
  (props: IconProps | IconFabricProps): React.ReactNode;
}

export const Icon: Icon = (props) => {
  const {
    ref,
    name,
    size,
    width,
    height,
    animation,
    duration,
    className,
    tabIndex,
    style,
    onClick,
  } = createIconProps(props);
  const finalWidth = size ?? width ?? 24;
  const finalHeight = size ?? height ?? finalWidth;

  return (
    <svg
      ref={ref}
      className={clsx(
        styles.icon,
        animation !== undefined && styles[animation],
        className,
      )}
      width={finalWidth}
      height={finalHeight}
      viewBox={`0 0 ${finalWidth} ${finalHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      style={{
        "--icon-animation-duration":
          duration !== undefined ? `${duration}ms` : undefined,
        ...style,
      }}
      tabIndex={tabIndex}
      onClick={onClick}
    >
      <use href={`/static/icons/icons.svg#${name}`} />
    </svg>
  );
};

const createIconProps = (props: IconProps | IconFabricProps) => {
  if (props.icon === undefined) {
    return {
      ...props,
    };
  }

  return typeof props.icon === "string"
    ? {
        name: props.icon,
        className: props.className,
      }
    : {
        ...props.icon,
        className: clsx(props.className, props.icon.className),
      };
};
